import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Sparkle, HandCoins, ShieldCheck, UsersThree, Target,
} from "@phosphor-icons/react";
import Logo from "@/components/Logo";

// Small hook that keeps document title + description in sync with each page.
// We deliberately avoid react-helmet — a tiny effect gives us the same SEO
// signal for CSR routes and doesn't add another dependency.
function usePageMeta({ title, description }) {
  useEffect(() => {
    if (title) document.title = title;
    if (description) {
      let el = document.querySelector('meta[name="description"]');
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", "description");
        document.head.appendChild(el);
      }
      el.setAttribute("content", description);
    }
  }, [title, description]);
}

const SECTIONS = [
  {
    id: "mission",
    icon: Target,
    label: "Why We Built MyResumeDesign",
    heading: "Our Mission",
    body: [
      "Searching for a new job is already challenging. Creating a professional résumé shouldn't add unnecessary cost or complexity.",
      "Many résumé builders require users to purchase monthly or yearly subscriptions, even when they only need to create or update a résumé once. For most job seekers, this isn't practical or cost-effective.",
      "MyResumeDesign was created to offer a simpler alternative — an easy-to-use résumé builder where users pay only when they need to download their résumé.",
    ],
  },
  {
    id: "pricing",
    icon: HandCoins,
    label: "Pay Only When You Need It",
    heading: "A Fair Pay-as-You-Go Model",
    body: [
      "Instead of locking users into recurring subscriptions, MyResumeDesign follows a straightforward pay-as-you-go approach.",
    ],
    bullets: [
      "Create résumés for free.",
      "Edit and customise unlimited times.",
      "Explore multiple professional templates.",
      "Pay only when downloading the final résumé.",
    ],
    footer: "This approach helps users avoid unnecessary subscription costs while providing access to premium-quality résumé designs.",
  },
  {
    id: "privacy",
    icon: ShieldCheck,
    label: "Your Privacy Matters",
    heading: "Privacy by Design",
    body: [
      "Your résumé contains personal and professional information that deserves to remain private. MyResumeDesign is built with privacy as a core principle.",
    ],
    bullets: [
      "We do not permanently store your personal information.",
      "We do not maintain a database of your résumés.",
      "Your résumé data remains within your active browser session.",
      "Once your session ends, your information is automatically cleared.",
    ],
    footer: "This means you remain in control of your data from start to finish.",
  },
  {
    id: "recruiter",
    icon: UsersThree,
    label: "Built With Recruiter Insight",
    heading: "Designed with Real Hiring Experience",
    body: [
      "Every résumé template and editing feature has been developed with practical recruitment experience in mind.",
      "The platform focuses on helping candidates present their experience clearly, professionally, and in a format that recruiters can quickly review.",
      "Whether you're a recent graduate or an experienced professional, MyResumeDesign helps you create résumés that are polished, ATS-friendly, and recruiter-ready.",
    ],
  },
];

export default function About() {
  usePageMeta({
    title: "About MyResumeDesign | Affordable AI Resume Builder",
    description:
      "Learn about MyResumeDesign, a recruiter-built resume builder offering a privacy-first, pay-as-you-go alternative to subscription-based resume tools.",
  });

  return (
    <div className="min-h-screen bg-white text-neutral-950 font-sans" data-testid="about-page">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-black/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 text-sm text-neutral-700 hover:text-black" data-testid="about-back-home">
              <ArrowLeft size={16} weight="bold" />
              <span className="hidden sm:inline">Home</span>
            </Link>
            <div className="h-4 w-px bg-black/20" />
            <Logo />
          </div>
          <Link to="/builder?welcome=1" className="btn-primary text-xs" data-testid="about-cta-build">
            Start building — Free
            <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-black/5">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16 lg:py-24">
          <div className="animate-fade-up">
            <div className="label-mono mb-6" data-testid="about-hero-label">
              <span className="inline-block w-6 h-px bg-black/40 align-middle mr-2" />
              About / MyResumeDesign
            </div>
            <h1
              className="font-heading font-black tracking-tighter leading-[0.95] text-4xl sm:text-5xl lg:text-7xl max-w-4xl"
              data-testid="about-hero-heading"
            >
              Built by a Recruitment Professional.
              <br />
              <span className="shimmer-text">Designed for Job Seekers.</span>
            </h1>
            <div className="mt-10 max-w-2xl text-base lg:text-lg text-neutral-600 leading-relaxed space-y-5">
              <p>
                MyResumeDesign was created by an experienced recruitment professional with years of expertise in hiring and reviewing thousands of résumés across industries.
              </p>
              <p>
                Having seen firsthand what recruiters look for in a well-crafted résumé, the goal was simple — to make professional résumé creation accessible, affordable, and privacy-focused for every job seeker.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Content sections */}
      <section className="border-b border-black/5">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16 lg:py-24 space-y-16 lg:space-y-24">
          {SECTIONS.map((s, i) => (
            <div
              key={s.id}
              className="grid grid-cols-12 gap-6 lg:gap-8 animate-fade-up"
              style={{ animationDelay: `${i * 0.06}s` }}
              data-testid={`about-section-${s.id}`}
            >
              <div className="col-span-12 lg:col-span-4">
                <div className="flex items-start gap-4">
                  <div className="border border-black/10 p-3">
                    <s.icon size={22} weight="duotone" className="text-[#2400FF]" />
                  </div>
                  <div>
                    <div className="label-mono text-neutral-500">
                      {String(i + 1).padStart(2, "0")} / {s.label}
                    </div>
                    <h2 className="mt-3 font-heading font-bold text-2xl lg:text-4xl tracking-tight leading-[1.05]">
                      {s.heading}
                    </h2>
                  </div>
                </div>
              </div>
              <div className="col-span-12 lg:col-span-7 lg:col-start-6 text-base text-neutral-700 leading-relaxed space-y-5">
                {s.body.map((p, pi) => (
                  <p key={pi}>{p}</p>
                ))}
                {s.bullets && (
                  <ul className="space-y-2 pt-1" data-testid={`about-bullets-${s.id}`}>
                    {s.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2">
                        <span className="inline-block w-1.5 h-1.5 bg-[#2400FF] mt-2 shrink-0 rounded-full" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {s.footer && <p className="text-neutral-600 pt-1">{s.footer}</p>}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Closing */}
      <section className="bg-neutral-950 text-white">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="grid grid-cols-12 gap-8 items-end">
            <div className="col-span-12 lg:col-span-8">
              <div className="label-mono text-neutral-400 mb-6">Closing note</div>
              <h2
                className="font-heading font-black tracking-tighter text-4xl lg:text-7xl leading-[0.95]"
                data-testid="about-closing-heading"
              >
                Simple.
                <br />
                Affordable.
                <br />
                <span className="shimmer-text">Professional.</span>
              </h2>
              <p className="mt-8 max-w-2xl text-neutral-400 leading-relaxed text-lg">
                Our goal is to remove unnecessary barriers from the résumé creation process by providing an intuitive, affordable, and privacy-first experience that helps candidates focus on what matters most — landing their next opportunity.
              </p>
            </div>
            <div className="col-span-12 lg:col-span-4 flex lg:justify-end">
              <Link
                to="/builder?welcome=1"
                className="inline-flex items-center gap-3 bg-white text-black px-6 py-4 text-sm font-medium rounded-sm hover:-translate-y-[1px] transition-all"
                data-testid="about-closing-cta"
              >
                Start building — Free
                <ArrowRight size={16} weight="bold" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-black/10 py-8">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-neutral-500">
          <div className="font-mono flex items-center gap-3">
            <Sparkle size={14} weight="fill" className="text-[#FF2A00]" />
            © 2026 MyResumeDesign
          </div>
          <div className="flex flex-wrap items-center gap-6 font-mono uppercase tracking-widest text-xs">
            <Link to="/about" className="hover:text-black">About</Link>
            <Link to="/contact" className="hover:text-black">Contact</Link>
            <Link to="/help" className="hover:text-black">Help</Link>
            <Link to="/privacy" className="hover:text-black">Privacy</Link>
            <Link to="/terms" className="hover:text-black">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
